"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { CashCounter } from "@/components/ui/cash-counter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";

import {
  getCreditScoreFeedback,
  getCreditScoreRange,
} from "@/lib/credit-score";
import { cn } from "@/lib/utils";

const creditScoreSchema = z.object({
  creditScore: z.number().min(300).max(850),
});

type CreditScoreForm = z.infer<typeof creditScoreSchema>;

function CreditScoreHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-0 right-0 text-gray-500 hover:text-gray-700"
        >
          <Info className="w-5 h-5" />
          <span className="sr-only">How to check your credit score</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>How to Check Your Credit Score for Free</DialogTitle>
          <DialogDescription>
            There are several reliable ways to check your credit score:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold">Annual Credit Report</h3>
            <p className="text-sm text-gray-600">
              Visit AnnualCreditReport.com for your free annual credit report
              from all three bureaus.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Credit Card Companies</h3>
            <p className="text-sm text-gray-600">
              Many credit card issuers provide free credit scores to their
              customers.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Credit Monitoring Services</h3>
            <p className="text-sm text-gray-600">
              Services like Credit Karma, Credit Sesame, and Experian offer free
              credit monitoring.
            </p>
          </div>
          <div className="pt-2 text-sm text-gray-500">
            Note: These services may show slightly different scores, but they'll
            give you a good idea of your credit standing.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreditScoreForm() {
  const { journeyData, updateJourney } = useJourneyData();
  const { toast } = useToast();
  const [isSliderActive, setIsSliderActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const previousValueRef = useRef<number>();
  const userInitiatedRef = useRef(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();

  const form = useForm<CreditScoreForm>({
    resolver: zodResolver(creditScoreSchema),
    values: {
      creditScore: journeyData?.creditScore || 700,
    },
  });

  const currentScore = form.watch("creditScore");
  const currentRange = getCreditScoreRange(currentScore);
  const feedback = getCreditScoreFeedback(currentScore);

  const handleSliderChange = (value: number[]) => {
    userInitiatedRef.current = true;
    form.setValue("creditScore", value[0]);
  };

  useEffect(() => {
    if (isDragging) {
      previousValueRef.current = currentScore;
      return;
    }

    if (!userInitiatedRef.current) return;
    if (previousValueRef.current === currentScore) return;

    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Set a new timeout to update after the user stops dragging
    updateTimeoutRef.current = setTimeout(() => {
      updateJourney.mutate(
        { creditScore: currentScore },
        {
          onSuccess: () => {
            const range = getCreditScoreRange(currentScore);
            toast({
              title: "Credit Score Updated",
              description: `Your credit score has been updated to ${currentScore}`,
              variant: "default",
            });
          },
        },
      );
      previousValueRef.current = currentScore;
    }, 500);

    return () => {
      clearTimeout(updateTimeoutRef.current);
    };
  }, [isDragging, currentScore, updateJourney]);

  return (
    <Form {...form}>
      <form className="space-y-8 max-w-2xl mx-auto relative">
        <CreditScoreHelpDialog />
        <FormField
          control={form.control}
          name="creditScore"
          render={({ field }) => (
            <FormItem className="space-y-10">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center space-y-4"
              >
                <CashCounter
                  value={field.value}
                  className="text-7xl font-bold text-gray-900"
                  baseDelay={50}
                  showDollarSign={false}
                  skipInitialAnimation={!!journeyData?.creditScore}
                />
                <div
                  className={cn(
                    "text-xl font-semibold flex items-center justify-center gap-2 px-6 py-2 rounded-full",
                    currentRange.color.replace("bg-", "text-"),
                  )}
                >
                  {currentRange.label}
                  {currentScore >= 740 ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    <AlertCircle className="w-6 h-6" />
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center space-y-2 px-4"
              >
                <p className="text-gray-700">{feedback.message}</p>
                {feedback.action && (
                  <p className="text-sm text-gray-600 font-semibold">
                    Tip: {feedback.action}
                  </p>
                )}
              </motion.div>

              <div className="relative w-[90%] mx-auto">
                <div className="absolute -top-3 left-0 right-0">
                  <div className="flex justify-between">
                    {currentRange.allRanges.map((range) => (
                      <div
                        key={range.label}
                        className={cn(
                          "h-1.5 flex-1",
                          range.color,
                          "first:rounded-l-full last:rounded-r-full",
                        )}
                      />
                    ))}
                  </div>
                </div>

                <FormControl>
                  <div
                    className="pt-6"
                    onMouseEnter={() => setIsSliderActive(true)}
                    onMouseLeave={() => {
                      setIsSliderActive(false);
                      setIsDragging(false);
                    }}
                    onMouseDown={() => setIsDragging(true)}
                    onMouseUp={() => setIsDragging(false)}
                    onTouchStart={() => setIsDragging(true)}
                    onTouchEnd={() => setIsDragging(false)}
                  >
                    <Slider
                      value={[field.value]}
                      min={300}
                      max={850}
                      step={1}
                      onValueChange={handleSliderChange}
                      className="py-4"
                      classNames={{
                        track: "h-2 bg-gray-200",
                        range: cn("h-2", currentRange.color),
                        thumb: cn(
                          "h-8 w-8 border-2",
                          isSliderActive && "scale-110",
                          currentRange.color.replace("bg-", "border-"),
                          "bg-white shadow-lg transition-all duration-200",
                        ),
                      }}
                    />
                  </div>
                </FormControl>
              </div>

              <div className="flex justify-between text-sm text-gray-500 px-2">
                {currentRange.allRanges.map((range) => (
                  <div key={range.label} className="text-center">
                    <div className="font-semibold">{range.label}</div>
                    <div className="text-xs">
                      {range.min}-{range.max}
                    </div>
                  </div>
                ))}
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
